import { addressToText } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { UserField } from '@dadohr/shared/domainTypes/experience';
import {
  CustomUserField,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { assertNever } from 'assert-never';
import firebase from 'firebase/app';
import { compact, keyBy } from 'lodash';
import React, { useMemo } from 'react';
import {
  formatPossiblyYearlessMoment,
  fromMoment,
  now,
  toMoment
} from '../../services/time';
import { getCalculatedFields, toFullName } from '../../services/user';
import { ItemSorter, ItemSorters } from '../GroupableList';
import { IColumn } from '../Table/VirtualizedSortable';
import { LazyUserById } from './LazyUser';

export type ColumnName =
  | 'name'
  | 'firstName'
  | 'preferredName'
  | 'lastName'
  | 'email'
  | 'personalEmail'
  | 'managerId'
  | 'role'
  | 'location'
  | 'department'
  | 'team'
  | 'hireDate'
  | 'dateOfBirth'
  | 'alternateId'
  | 'actions'
  | string;

export type D = Doc<IUser>;
export type O = {
  dateFormat: string;
  additionalUsers: {
    [key: string]: Doc<IUser>;
  };
  highlightedFields?: Record<string, Set<string>>;
};

export type UserTableColumn = IColumn<D, ColumnName, O>;

// @guide Add a field to the IUser object - 3.3 Add column renderer
//
// A renderer needs to be specified to show this in all UserLists.
//
//
// @guide Add a calculated field - 3.1 Define a rendering column for User tables
//
// Additionally if you want to make the field sortable, don't forget to define a sorter
export const COLUMNS: UserTableColumn[] = [
  {
    key: 'name',
    head: () => 'Full Name',
    matchString: () => 'Full Name',
    cell: (d) => {
      return toFullName(d.data);
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'firstName',
    head: () => 'First Name',
    matchString: () => 'First Name',
    cell: (d) => {
      return d.data.firstName;
    },
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 2
  },
  {
    key: 'preferredName',
    head: () => 'Preferred Name',
    matchString: () => 'Preferred Name',
    cell: (d) => {
      return d.data.preferredName;
    },
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 2
  },
  {
    key: 'lastName',
    head: () => 'Last Name',
    matchString: () => 'Last Name',
    cell: (d) => {
      return d.data.lastName;
    },
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 2
  },
  {
    key: 'email',
    head: () => 'Work Email',
    matchString: () => 'Work Email',
    cell: (d) => {
      return d.data.email;
    },
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 2
  },
  {
    key: 'personalEmail',
    head: () => 'Personal Email',
    matchString: () => 'Personal Email',
    cell: (d) => {
      return d.data.personalEmail;
    },
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 2
  },
  {
    key: 'role',
    head: () => 'Role',
    matchString: () => 'Role',
    cell: (d) => d.data.role,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'managerId',
    head: () => 'Manager',
    matchString: () => 'Manager',
    cell: (d, o) =>
      d.data.managerId && (
        <LazyUserById
          userId={d.data.managerId}
          otherUsers={o.additionalUsers}
        />
      ),
    sortable: false,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'location',
    head: () => 'Location',
    matchString: () => 'Location',
    cell: (d) => d.data.location,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'department',
    head: () => 'Department',
    matchString: () => 'Department',
    cell: (d) => d.data.department,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'team',
    head: () => 'Team',
    matchString: () => 'Team',
    cell: (d) => d.data.team,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'address',
    head: () => 'Address',
    matchString: () => 'Address',
    cell: (d) => addressToText(d.data.address),
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },
  {
    key: 'phoneNumber',
    head: () => 'Phone Number',
    matchString: () => 'Phone Number',
    cell: (d) => d.data.phoneNumber,
    sortable: true,
    align: 'left',
    width: 100,
    flexGrow: 2
  },

  {
    key: 'hireDate',
    head: () => 'First day at work',
    matchString: () => 'First day at work',
    cell: (d, o) => {
      return d.data.hireDate
        ? toMoment(d.data.hireDate).format(o.dateFormat)
        : '';
    },
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  },

  {
    key: 'terminationDate',
    head: () => 'Last day at work',
    matchString: () => 'Last day at work',
    cell: (d, o) => {
      return d.data.terminationDate
        ? toMoment(d.data.terminationDate).format(o.dateFormat)
        : '';
    },
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  },
  {
    key: 'dateOfBirth',
    head: () => 'Date of Birth',
    matchString: () => 'Date of Birth',
    cell: (d, o) => {
      return d.data.dateOfBirth
        ? formatPossiblyYearlessMoment(
            toMoment(d.data.dateOfBirth),
            o.dateFormat,
            true
          )
        : '';
    },
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  },
  {
    key: 'alternateId',
    head: () => 'Alternate ID',
    matchString: () => 'Alternate ID',
    cell: (d, o) => {
      return d.data.alternateId || '';
    },
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  },
  {
    key: 'anniversaryDate',
    head: () => 'Anniversary Date',
    matchString: () => 'Anniversary Date',
    cell: (d, o) => {
      const { calculatedFields } = getCalculatedFields(d.data, now());
      const anniversary = calculatedFields.recurringDate.hireDate?.current;
      return anniversary ? toMoment(anniversary.date).format(o.dateFormat) : '';
    },
    align: 'left',
    sortable: false,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'anniversaryCount',
    head: () => 'Anniversary Count',
    matchString: () => 'Anniversary Count',
    cell: (d) => {
      const { calculatedFields } = getCalculatedFields(d.data, now());
      const anniversary = calculatedFields.recurringDate.hireDate?.current;
      return anniversary ? anniversary.count : '';
    },
    align: 'left',
    sortable: false,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'nextBirthday',
    head: () => 'Next Birthday',
    matchString: () => 'Next Birthday',
    cell: (d, o) => {
      const { calculatedFields } = getCalculatedFields(d.data, now());
      const nextBirthday = calculatedFields.recurringDate.dateOfBirth?.current;
      return nextBirthday
        ? toMoment(nextBirthday.date).format(o.dateFormat)
        : '';
    },
    align: 'left',
    sortable: false,
    width: 120,
    flexGrow: 1
  }
];

export type UserTableItemSorters = ItemSorters<D, O>;

// @guide Add a field to the IUser object - 3.4 Add column sorter
export const SORTERS: UserTableItemSorters = {
  name: {
    key: 'name',
    items: {
      sort: [(d) => d.data.lastName, (d) => d.data.preferredName],
      dir: 'asc'
    }
  },
  firstName: {
    key: 'firstName',
    items: {
      sort: (d) => d.data.firstName,
      dir: 'asc'
    }
  },
  preferredName: {
    key: 'preferredName',
    items: {
      sort: (d) => d.data.preferredName,
      dir: 'asc'
    }
  },
  lasName: {
    key: 'lastName',
    items: {
      sort: (d) => d.data.lastName,
      dir: 'asc'
    }
  },
  email: {
    key: 'email',
    items: { sort: (d) => d.data.email, dir: 'asc' }
  },
  personalEmail: {
    key: 'personalEmail',
    items: { sort: (d) => d.data.personalEmail, dir: 'asc' }
  },
  role: {
    key: 'role',
    items: { sort: (d) => d.data.role, dir: 'asc' }
  },
  location: {
    key: 'location',
    items: { sort: (d) => d.data.location, dir: 'asc' }
  },
  department: {
    key: 'department',
    items: { sort: (d) => d.data.department, dir: 'asc' }
  },
  team: {
    key: 'team',
    items: { sort: (d) => d.data.team, dir: 'asc' }
  },
  address: {
    key: 'address',
    items: {
      sort: (d) => (d.data.address ? addressToText(d.data.address) : 'ZZZZZZZ'),
      dir: 'asc'
    }
  },
  phoneNumber: {
    key: 'phoneNumber',
    items: {
      sort: (d) => d.data.phoneNumber,
      dir: 'asc'
    }
  },
  hireDate: {
    key: 'hireDate',
    items: {
      sort: (d) => (d.data.hireDate ? d.data.hireDate.toMillis() : Infinity),
      dir: 'asc'
    }
  },

  terminationDate: {
    key: 'terminationDate',
    items: {
      sort: (d) =>
        d.data.terminationDate ? d.data.terminationDate.toMillis() : Infinity,
      dir: 'asc'
    }
  },

  dateOfBirth: {
    key: 'dateOfBirth',
    items: {
      sort: (d) =>
        d.data.dateOfBirth
          ? fromMoment(
              toMoment(d.data.dateOfBirth).set('year', 1900)
            ).toMillis()
          : Infinity,
      dir: 'asc'
    }
  }
};

// @guide Add a field to the IUser object - 3.4 Add to default columns
//
// This is optional - decide if this field should be shown as part of
// one of our default sets for user columns
export const ALL_COLUMNS: ColumnName[] = [
  'name',
  'email',
  'role',
  'location',
  'department',
  'team',
  'hireDate',
  'terminationDate'
];

export const ALL_COLUMNS_INCL_PERS_EMAIL: ColumnName[] = [
  'name',
  'email',
  'personalEmail',
  'managerId',
  'role',
  'location',
  'department',
  'hireDate',
  'terminationDate'
];
export const SMALL_COLUMNS: ColumnName[] = [
  'name',
  'role',
  'location',
  'department',
  'hireDate'
];

const getCustomCell = (
  f: CustomUserField
): ((d: D, o: O) => React.ReactNode) => {
  switch (f.type) {
    case 'string':
      return (d) => {
        const value = d.data.customFields[f.key];

        // Boolean values can land in a string-typed custom field via
        // integration imports (e.g., Greenhouse Yes/No fields write
        // `true`/`false`). Render BEFORE the falsy check so `false` shows
        // as "No" instead of being swallowed by `if (!value)`.
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';

        if (!value) return '';

        if (typeof value === 'string') return value;
        
        // Handle firestore timestamp objects specifically
        if (typeof value === 'object' && typeof (value as any).seconds === 'number') {
          console.warn(`Custom field "${f.key}" is typed as string but contains timestamp:`, value);

          const date = (value as any).toDate ? (value as any).toDate() : new Date((value as any).seconds * 1000);
          return date.toLocaleDateString();
        }

        if (value instanceof Date) {
          console.warn(`Custom field "${f.key}" is configured as string but contains Date:`, value);
          return value.toLocaleDateString();
        }
        
        console.warn(`Custom field "${f.key}" is configured as string but contains ${typeof value}:`, value);
        return String(value);
      };
    case 'number':
      return (d) => d.data.customFields[f.key] ?? '';
    case 'boolean':
      return (d) => (d.data.customFields[f.key] ? 'Yes' : 'No'); // need to figure out how to deal with booleans
    case 'date':
    case 'smartDate':
      return (d, o) => {
        const x = d.data.customFields[f.key];
        if (x instanceof firebase.firestore.Timestamp) {
          return toMoment(x).format(o.dateFormat);
        }
      };
    case 'smartText':
      return (d) => d.data.customFields[f.key] ?? '';
    case 'relationship':
      return (d, o) => {
        const x = d.data.customFields[f.key];
        if (typeof x === 'string' && x) {
          return <LazyUserById userId={x} otherUsers={o.additionalUsers} />;
        }
        return null;
      };
    case 'address':
      return (d, o) => {
        const x: any = d.data.customFields[f.key];
        if (!x) {
          return null;
        }
        // double check type?
        return addressToText(x);
      };
    default:
      return assertNever(f);
  }
};

const getAlignment = (f: CustomUserField) => {
  switch (f.type) {
    case 'string':
    case 'boolean':
    case 'relationship':
    case 'address':
    case 'smartText':
      return 'left';
    case 'date':
    case 'smartDate':
    case 'number':
      return 'right';
    default:
      return assertNever(f);
  }
};

export const generateCustomFieldColumn = (
  f: CustomUserField
): UserTableColumn => {
  return {
    key: toCustomFieldKey(f.key),
    head: () => f.label,
    matchString: () => f.label,
    cell: getCustomCell(f),
    align: getAlignment(f),
    sortable: f.type !== 'relationship',
    width: 120,
    flexGrow: f.type === 'boolean' ? 0 : 1
  };
};

const generateCustomFieldSorter = (f: CustomUserField): ItemSorter<D, O> => {
  return {
    key: toCustomFieldKey(f.key),
    items: {
      sort: (d) => {
        const v = d.data.customFields[f.key];
        if (f.type === 'relationship') {
          // TODO implement relationship sorter
          return '';
        }
        if (f.type === 'address') {
          return addressToText(v as any);
        }
        if (v instanceof firebase.firestore.Timestamp) {
          return v.toMillis();
        }
        return (v as any) ?? '';
      },
      dir: 'asc'
    }
  };
};

export const DETAILED_NAME_COLUMNS: UserField[] = [
  'firstName',
  'preferredName',
  'lastName'
];

// - will take an optional filter to allow column selection
// - pass custom fields in, so that it can be used in other
//   contexts like the admin app
// - the order of the selection list is also used to determine
//   the final column order
export const useUserTableColumnsAndSorters = (
  customFields: CustomUserField[] = EMPTY_ARR,
  selectedColumnsInOrder: ColumnName[] = EMPTY_ARR
): { columns: UserTableColumn[]; sorters: UserTableItemSorters } => {
  return useMemo(() => {
    const allColumns = [
      ...COLUMNS,
      ...customFields.map(generateCustomFieldColumn)
    ];
    const sorters = { ...SORTERS };
    customFields.forEach(
      (f) => (sorters[toCustomFieldKey(f.key)] = generateCustomFieldSorter(f))
    );

    if (!selectedColumnsInOrder.length) {
      return { columns: allColumns, sorters };
    }
    const allColumnsByKey = keyBy(allColumns, (c) => c.key);
    return {
      columns: compact(selectedColumnsInOrder.map((k) => allColumnsByKey[k])),
      sorters
    };
  }, [customFields, selectedColumnsInOrder]);
};
